import type {
	DataListColumnVisibility,
	DataListColumnWidth,
} from '@arritech/ui/DataList/DataList.model';
import type {
	DataListPreferencesEntry,
	DataListSortPreference,
} from '../settings.model';
import {
	isSameArray,
	isSameColumnVisibility,
	isSameColumnWidths,
	normalizeColumnVisibility,
	normalizeColumnWidths,
	persistDataListPreferences,
} from '../settings.utils';

/**
 * DataList preference actions.
 *
 * Persists sort, column order, column visibility and page-size preferences
 * per table key and schedules backend sync.
 */
export const dataListPreferencesActions = {
	/**
	 * Returns sort preference for a DataList key.
	 */
	getSort(this: any, key: string): DataListSortPreference | null {
		if (!key) {
			return null;
		}

		const sort = this.dataListPreferences[key]?.sort;
		if (!sort?.prop || !sort?.order) {
			return null;
		}

		return sort;
	},
	/**
	 * Stores sort preference for a DataList key.
	 */
	setSort(this: any, key: string, sort: DataListSortPreference): void {
		if (!key || !sort?.prop || !sort?.order) {
			return;
		}

		const currentSort = this.dataListPreferences[key]?.sort;
		const isSameSort =
			currentSort?.prop === sort.prop && currentSort?.order === sort.order;
		if (isSameSort) {
			return;
		}

		this.dataListPreferences = {
			...this.dataListPreferences,
			[key]: {
				...(this.dataListPreferences[key] || {}),
				sort,
			},
		};
		persistDataListPreferences(this.dataListPreferences);
		this.queueUIPreferencesUpsert();
	},
	/**
	 * Clears sort preference while preserving other DataList preferences.
	 */
	clearSort(this: any, key: string): void {
		if (!key || !this.dataListPreferences[key]) {
			return;
		}

		const current = this.dataListPreferences[key];
		const next = { ...this.dataListPreferences };
		const nextEntry: DataListPreferencesEntry = {};
		if (current?.columnOrder?.length) {
			nextEntry.columnOrder = current.columnOrder;
		}
		if (current?.columnVisibility?.length) {
			nextEntry.columnVisibility = current.columnVisibility;
		}
		if (current?.columnWidths?.length) {
			nextEntry.columnWidths = current.columnWidths;
		}
		if (
			typeof current?.perPage === 'number' &&
			Number.isFinite(current.perPage) &&
			current.perPage > 0
		) {
			nextEntry.perPage = current.perPage;
		}

		if (Object.keys(nextEntry).length) {
			next[key] = nextEntry;
		} else {
			delete next[key];
		}
		this.dataListPreferences = next;
		persistDataListPreferences(this.dataListPreferences);
		this.queueUIPreferencesUpsert();
	},
	/**
	 * Returns column order preference for a DataList key.
	 */
	getColumnOrder(this: any, key: string): string[] | null {
		if (!key) {
			return null;
		}

		const columnOrder = this.dataListPreferences[key]?.columnOrder;
		if (!Array.isArray(columnOrder) || columnOrder.length === 0) {
			return null;
		}

		return columnOrder;
	},
	/**
	 * Returns normalized column visibility preference for a DataList key.
	 */
	getColumnVisibility(
		this: any,
		key: string
	): DataListColumnVisibility[] | null {
		if (!key) {
			return null;
		}

		const columnVisibility = this.dataListPreferences[key]?.columnVisibility;
		if (!Array.isArray(columnVisibility) || columnVisibility.length === 0) {
			return null;
		}

		const normalized = normalizeColumnVisibility(columnVisibility);
		return normalized.length ? normalized : null;
	},
	/**
	 * Stores column visibility preference for a DataList key.
	 */
	setColumnVisibility(
		this: any,
		key: string,
		columnVisibility: DataListColumnVisibility[]
	): void {
		if (
			!key ||
			!Array.isArray(columnVisibility) ||
			columnVisibility.length === 0
		) {
			return;
		}

		const normalized = normalizeColumnVisibility(columnVisibility);
		if (!normalized.length) {
			return;
		}

		const currentVisibility =
			this.dataListPreferences[key]?.columnVisibility || [];
		if (isSameColumnVisibility(currentVisibility, normalized)) {
			return;
		}

		this.dataListPreferences = {
			...this.dataListPreferences,
			[key]: {
				...(this.dataListPreferences[key] || {}),
				columnVisibility: normalized,
			},
		};
		persistDataListPreferences(this.dataListPreferences);
		this.queueUIPreferencesUpsert();
	},
	/**
	 * Clears column visibility preference while preserving other settings.
	 */
	clearColumnVisibility(this: any, key: string): void {
		if (!key || !this.dataListPreferences[key]) {
			return;
		}

		const current = this.dataListPreferences[key];
		const next = { ...this.dataListPreferences };
		const nextEntry: DataListPreferencesEntry = {};
		if (current?.sort) {
			nextEntry.sort = current.sort;
		}
		if (current?.columnOrder?.length) {
			nextEntry.columnOrder = current.columnOrder;
		}
		if (
			typeof current?.perPage === 'number' &&
			Number.isFinite(current.perPage) &&
			current.perPage > 0
		) {
			nextEntry.perPage = current.perPage;
		}
		if (current?.columnWidths?.length) {
			nextEntry.columnWidths = current.columnWidths;
		}

		if (Object.keys(nextEntry).length) {
			next[key] = nextEntry;
		} else {
			delete next[key];
		}
		this.dataListPreferences = next;
		persistDataListPreferences(this.dataListPreferences);
		this.queueUIPreferencesUpsert();
	},
	/**
	 * Stores column order preference for a DataList key.
	 */
	setColumnOrder(this: any, key: string, columnOrder: string[]): void {
		if (!key || !Array.isArray(columnOrder) || columnOrder.length === 0) {
			return;
		}

		const currentOrder = this.dataListPreferences[key]?.columnOrder || [];
		if (isSameArray(currentOrder, columnOrder)) {
			return;
		}

		this.dataListPreferences = {
			...this.dataListPreferences,
			[key]: {
				...(this.dataListPreferences[key] || {}),
				columnOrder,
			},
		};
		persistDataListPreferences(this.dataListPreferences);
		this.queueUIPreferencesUpsert();
	},
	/**
	 * Clears column order preference while preserving other settings.
	 */
	clearColumnOrder(this: any, key: string): void {
		if (!key || !this.dataListPreferences[key]) {
			return;
		}

		const current = this.dataListPreferences[key];
		const next = { ...this.dataListPreferences };
		const nextEntry: DataListPreferencesEntry = {};
		if (current?.sort) {
			nextEntry.sort = current.sort;
		}
		if (current?.columnVisibility?.length) {
			nextEntry.columnVisibility = current.columnVisibility;
		}
		if (current?.columnWidths?.length) {
			nextEntry.columnWidths = current.columnWidths;
		}
		if (
			typeof current?.perPage === 'number' &&
			Number.isFinite(current.perPage) &&
			current.perPage > 0
		) {
			nextEntry.perPage = current.perPage;
		}

		if (Object.keys(nextEntry).length) {
			next[key] = nextEntry;
		} else {
			delete next[key];
		}
		this.dataListPreferences = next;
		persistDataListPreferences(this.dataListPreferences);
		this.queueUIPreferencesUpsert();
	},
	/**
	 * Returns page-size preference for a DataList key.
	 */
	getPerPage(this: any, key: string): number | null {
		if (!key) {
			return null;
		}

		const perPage = this.dataListPreferences[key]?.perPage;
		if (
			typeof perPage !== 'number' ||
			!Number.isFinite(perPage) ||
			perPage <= 0
		) {
			return null;
		}

		return perPage;
	},
	/**
	 * Stores page-size preference for a DataList key.
	 */
	setPerPage(this: any, key: string, perPage: number): void {
		if (
			!key ||
			typeof perPage !== 'number' ||
			!Number.isFinite(perPage) ||
			perPage <= 0
		) {
			return;
		}

		if (this.dataListPreferences[key]?.perPage === perPage) {
			return;
		}

		this.dataListPreferences = {
			...this.dataListPreferences,
			[key]: {
				...(this.dataListPreferences[key] || {}),
				perPage,
			},
		};
		persistDataListPreferences(this.dataListPreferences);
		this.queueUIPreferencesUpsert();
	},
	/**
	 * Clears page-size preference while preserving other DataList settings.
	 */
	clearPerPage(this: any, key: string): void {
		if (!key || !this.dataListPreferences[key]) {
			return;
		}

		const current = this.dataListPreferences[key];
		const next = { ...this.dataListPreferences };
		const nextEntry: DataListPreferencesEntry = {};
		if (current?.sort) {
			nextEntry.sort = current.sort;
		}
		if (current?.columnOrder?.length) {
			nextEntry.columnOrder = current.columnOrder;
		}
		if (current?.columnVisibility?.length) {
			nextEntry.columnVisibility = current.columnVisibility;
		}
		if (current?.columnWidths?.length) {
			nextEntry.columnWidths = current.columnWidths;
		}

		if (Object.keys(nextEntry).length) {
			next[key] = nextEntry;
		} else {
			delete next[key];
		}
		this.dataListPreferences = next;
		persistDataListPreferences(this.dataListPreferences);
		this.queueUIPreferencesUpsert();
	},
	/**
	 * Returns normalized column width preference for a DataList key.
	 */
	getColumnWidths(this: any, key: string): DataListColumnWidth[] | null {
		if (!key) {
			return null;
		}

		const columnWidths = this.dataListPreferences[key]?.columnWidths;
		if (!Array.isArray(columnWidths) || columnWidths.length === 0) {
			return null;
		}

		const normalized = normalizeColumnWidths(columnWidths);
		return normalized.length ? normalized : null;
	},
	/**
	 * Stores column width preference for a DataList key.
	 */
	setColumnWidths(
		this: any,
		key: string,
		columnWidths: DataListColumnWidth[]
	): void {
		if (!key || !Array.isArray(columnWidths) || columnWidths.length === 0) {
			return;
		}

		const normalized = normalizeColumnWidths(columnWidths);
		if (!normalized.length) {
			return;
		}

		const currentWidths = this.dataListPreferences[key]?.columnWidths || [];
		if (isSameColumnWidths(currentWidths, normalized)) {
			return;
		}

		this.dataListPreferences = {
			...this.dataListPreferences,
			[key]: {
				...(this.dataListPreferences[key] || {}),
				columnWidths: normalized,
			},
		};
		persistDataListPreferences(this.dataListPreferences);
		this.queueUIPreferencesUpsert();
	},
	/**
	 * Clears column width preference while preserving other settings.
	 */
	clearColumnWidths(this: any, key: string): void {
		if (!key || !this.dataListPreferences[key]) {
			return;
		}

		const current = this.dataListPreferences[key];
		const next = { ...this.dataListPreferences };
		const nextEntry: DataListPreferencesEntry = {};
		if (current?.sort) {
			nextEntry.sort = current.sort;
		}
		if (current?.columnOrder?.length) {
			nextEntry.columnOrder = current.columnOrder;
		}
		if (current?.columnVisibility?.length) {
			nextEntry.columnVisibility = current.columnVisibility;
		}
		if (
			typeof current?.perPage === 'number' &&
			Number.isFinite(current.perPage) &&
			current.perPage > 0
		) {
			nextEntry.perPage = current.perPage;
		}

		if (Object.keys(nextEntry).length) {
			next[key] = nextEntry;
		} else {
			delete next[key];
		}
		this.dataListPreferences = next;
		persistDataListPreferences(this.dataListPreferences);
		this.queueUIPreferencesUpsert();
	},
};
